<template>
  <RevCard>
    <EmptyState
      v-if="!hasData && !pending"
      class="p-24"
      :description="i18n(translations.noDataDescription)"
      :surtitle="i18n(translations.noDataSurtitle)"
      :title="i18n(translations.noDataTitle)"
    />
    <template v-if="hasData || pending">
      <div class="p-24">
        <h2 class="heading-2 mb-8">
          {{ i18n(translations.title) }}
        </h2>
        <div class="body-2">
          {{ i18n(translations.description) }}
        </div>
      </div>
    </template>
    <WithSkeletonTableLoader :columns :is-loading="pending">
      <PaginatedTable
        v-if="hasData"
        :columns
        :current-page
        :data="data as GetGoodwillGestures"
        :page-size="PAGE_SIZE"
        @page-change="handlePaginationClick"
      >
        <template #body-goodwillNumber="{ item }">
          {{ item.id }}
        </template>
        <template #body-orderNumber="{ item }">
          <RevLink
            v-if="item.orderId"
            :to="{
              name: ROUTE_NAMES.ORDERS.INDEX,
              query: {
                orderId: item.orderId,
              },
            }"
          >
            {{ item.orderId }}
          </RevLink>
          <RevLink
            v-if="item.buybackOrderId"
            :to="{
              name: ROUTE_NAMES.ORDERS.INDEX,
              params: {
                id: item.buybackOrderId,
              },
            }"
            underlined
          >
            {{ item.buybackOrderId }}
          </RevLink>
        </template>
        <template #body-amount="{ item }">
          {{ i18n.price(item.sellerAmount) }}
        </template>
        <template #body-reason="{ item }">
          {{ getReasonLabel(item.category) }}
        </template>
        <template #body-creationDate="{ item }">
          <span data-allow-mismatch>
            {{ i18n.date(new Date(item.creationDate), FULL_DATE_FORMAT) }}
          </span>
        </template>
        <template #body-status="{ item }">
          <RevTag
            :label="item.state.label"
            :variant="getStatusVariant(item.state.code)"
          />
        </template>
      </PaginatedTable>
    </WithSkeletonTableLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage, navigateTo, useRoute } from '#imports'
import { computed } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import type { GetGoodwillGestures } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'

import reasonTranslations from '~/constants/reasons.translations'
import { ROUTE_NAMES } from '~/constants/routes'

import {
  DEFAULT_PAGE,
  FULL_DATE_FORMAT,
  PAGE_SIZE,
} from '../../Invoices.constants'
import { getStatusVariant } from '../../Invoices.utils'
import EmptyState from '../../components/EmptyState/EmptyState.vue'
import WithSkeletonTableLoader from '../../components/WithSkeletonTableLoader/WithSkeletonTableLoader.vue'

import translations from './GoodwillGestures.translations'

const i18n = useI18n()
const route = useRoute()
const { openErrorToast } = useTheToast()

const currentPage = computed(
  () => parseInt(route.query.page?.toString() || '', 10) || DEFAULT_PAGE,
)

const keys = [
  'goodwillNumber',
  'orderNumber',
  'amount',
  'reason',
  'creationDate',
  'status',
] as (keyof typeof translations)[]

const columns = keys.map((key) => ({ key, label: i18n(translations[key]) }))

const { data, error, pending } = await useHttpLazyFetch(
  merchantsAPI.getGoodwillGestures,
  {
    queryParams: { page: currentPage },
    watch: [currentPage],
  },
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const hasData = computed(() => !!data.value?.results?.length)

function getReasonLabel(category: string | null) {
  if (!category) return ''

  return i18n(
    reasonTranslations[category as keyof typeof reasonTranslations] || category,
  )
}

async function handlePaginationClick({ page }: { page: number }) {
  await navigateTo({ ...route, query: { page } })
}
</script>
